import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import ElasticHistoryButton from "../components/ElasticHistoryButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { ScanLine, Search, Coins, Gift, Sparkles, Plus, CreditCard, RefreshCw, QrCode, Ticket, X, Printer, Package, Trash2, Image as ImageIcon, Camera, Wallet, Undo2, Eye, Bluetooth } from "lucide-react";
import { printPoints, printReward, autoPrint, isSupported as thermalSupported } from "../lib/escpos";
import ThermalQuickButton from "../components/ThermalQuickButton";
import QrScanner from "../components/QrScanner";
import PickupRedeem, { isPickupCode } from "../components/PickupRedeem";
import { useNavigate } from "react-router-dom";
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import { useAuth } from "../context/AuthContext";
import { buildReceiptHtml, openReceiptPrintWindow } from "../lib/receipt";

export default function Counter() {
  const navigate = useNavigate();
  const { settings } = useAppSettings();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState("");
  const [rewards, setRewards] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [collections, setCollections] = useState([]);
  const [earnCollIds, setEarnCollIds] = useState(null);
  const [custCollections, setCustCollections] = useState([]);
  const activeColls = collections.filter((c) => c.status === "active");
  const defaultColl = collections.find((c) => c.is_default);
  const selectedCollIds = earnCollIds ?? activeColls.filter((c) => c.pos_default).map((c) => c.id);
  const toggleColl = (id) => setEarnCollIds(selectedCollIds.includes(id) ? selectedCollIds.filter((x) => x !== id) : [...selectedCollIds, id]);
  const collBalance = (collId) => { const c = custCollections.find((x) => x.id === (collId || defaultColl?.id)); return c ? c.balance : (collId && collId !== defaultColl?.id ? 0 : customer?.points || 0); };
  const [scanOpen, setScanOpen] = useState(false);
  const [scanMode, setScanMode] = useState("card"); // "card" | "coupon"
  const [pickup, setPickup] = useState(null); // { code } → dialog Ritiro premio
  const [couponCode, setCouponCode] = useState("");
  // Auto-consumo: quando true, appena l'operatore inserisce un importo > 0 parte
  // automaticamente la conferma scontrino (evita al cassiere il tap "Registra").
  const [couponAutoFire, setCouponAutoFire] = useState(false);
  const [cashbackUse, setCashbackUse] = useState("");
  const [cbCfg, setCbCfg] = useState(null);
  useEffect(() => { api.get("/settings/cashback").then((r) => setCbCfg(r.data)).catch(() => setCbCfg(null)); }, []);
  const [couponPickerOpen, setCouponPickerOpen] = useState(false);
  const [couponPickerRows, setCouponPickerRows] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [lastEarn, setLastEarn] = useState(null);
  const [products, setProducts] = useState([]);
  const [dailyProduct, setDailyProduct] = useState(null);
  const [items, setItems] = useState([]);
  const [productPickOpen, setProductPickOpen] = useState(false);
  const [galleryPreview, setGalleryPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Anteprima scontrino (dialog con iframe) prima della stampa reale
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  // Sede operativa: quando l'admin/Owner sceglie una sede, tutti i movimenti
  // registrati da qui vengono associati a quella sede. Lo staff ha branch_id
  // fissato dal server dal proprio profilo.
  const [branches, setBranches] = useState([]);
  const [operatorBranchId, setOperatorBranchId] = useState(
    () => (typeof localStorage !== "undefined" && localStorage.getItem("pos:branch_id")) || ""
  );

  useEffect(() => {
    // Serve sia all'admin (per il selettore "Operi come sede X") sia allo staff
    // (per stampare i dati corretti della propria sede sullo scontrino).
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    if (operatorBranchId) localStorage.setItem("pos:branch_id", operatorBranchId);
    else localStorage.removeItem("pos:branch_id");
  }, [operatorBranchId]);

  const loadCtx = async () => {
    // Quando l'Owner ha scelto "Operi come sede X" filtriamo automaticamente
    // catalogo premi e prodotti per quella sede. Per staff, operatorBranchId
    // è "" e il backend rispetta già `user.branch_id` sui filtri.
    const scopeParams = { only_active: true };
    if (isAdmin && operatorBranchId) scopeParams.branch_id = operatorBranchId;
    const [r, c, p, dp, col] = await Promise.all([
      api.get("/rewards", { params: scopeParams }),
      api.get("/campaigns/active"),
      api.get("/products", { params: scopeParams }),
      api.get("/daily-product", { params: isAdmin && operatorBranchId ? { branch_id: operatorBranchId } : {} }).catch(() => ({ data: null })),
      api.get("/collections").catch(() => ({ data: [] })),
    ]);
    setRewards(r.data);
    setCampaign(c.data);
    setProducts(p.data);
    setDailyProduct(dp.data || null);
    setCollections(col.data || []);
  };

  useEffect(() => { loadCtx(); /* eslint-disable-next-line */ }, [operatorBranchId]);

  // Reset auto-fire flag whenever the operator picks a different customer or clears the code
  useEffect(() => { if (!customer) setCouponAutoFire(false); }, [customer?.id]);
  useEffect(() => { if (!couponCode.trim()) setCouponAutoFire(false); }, [couponCode]);

  // AUTO-CONSUMO COUPON DOPO SCAN: quando un coupon è stato scansionato e validato
  // (couponAutoFire=true) e l'operatore inserisce un importo > 0, apri automaticamente
  // il dialog di conferma dopo un piccolo debounce (l'operatore può ancora fermarlo).
  useEffect(() => {
    if (!couponAutoFire) return;
    if (confirmOpen) return; // già aperto
    const eur = parseFloat(amount);
    if (!eur || eur <= 0) return;
    if (!customer) return;
    const t = setTimeout(() => {
      setConfirmOpen(true);
      toast.info("Auto-conferma da coupon scansionato — verifica e premi Conferma");
    }, 1400);
    return () => clearTimeout(t);
  }, [couponAutoFire, amount, customer, confirmOpen]);

  const search = async (override) => {
    const value = (override ?? query).trim();
    if (!value) return;
    setSearching(true);
    setSearchResults([]);
    try {
      // Numeric-only queries with >7 digits are treated as phone numbers → direct lookup for backwards-compat.
      // Everything else (names, cognomi, partial cards, or emails) goes through the fuzzy /customers/search.
      const digitsOnly = value.replace(/\D/g, "");
      const isPhone = /^[+0-9\s]+$/.test(value) && digitsOnly.length > 7;
      if (isPhone) {
        try {
          const { data } = await api.get("/customers/lookup", { params: { phone: value } });
          setCustomer(data);
          return;
        } catch (_) {
          // fall through to fuzzy search
        }
      }
      const { data } = await api.get("/customers/search", { params: { q: value, limit: 20 } });
      if (!data || data.length === 0) {
        toast.error("Nessun cliente trovato");
        setCustomer(null);
      } else if (data.length === 1) {
        setCustomer(data[0]);
        api.get(`/customers/${data[0].id}`).then((r) => setCustomer((cur) => (cur && cur.id === r.data.id ? { ...cur, ...r.data } : cur))).catch(() => {});
      } else {
        setSearchResults(data);
        setCustomer(null);
        toast.info(`${data.length} clienti trovati — seleziona`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setCustomer(null);
    } finally {
      setSearching(false);
    }
  };

  const pickResult = (c) => {
    setCustomer(c);
    api.get(`/customers/${c.id}`).then((r) => setCustomer((cur) => (cur && cur.id === r.data.id ? { ...cur, ...r.data } : cur))).catch(() => {});
    setSearchResults([]);
    setQuery(c.card_number || c.name);
  };

  const onScanDecoded = async (decoded) => {
    setScanOpen(false);
    const value = (decoded || "").trim();
    if (isPickupCode(value)) { setPickup({ code: value.toUpperCase() }); return; }
    if (scanMode === "coupon") {
      const code = value.toUpperCase();
      setCouponCode(code);
      setScanMode("card");
      // One-tap validation: if a customer is already selected, cross-check the
      // code against their active coupons and give the cashier a clear feedback.
      if (customer?.id) {
        try {
          const { data } = await api.get(`/customers/${customer.id}/coupons/active`);
          const match = (data || []).find((cp) => (cp.code || "").toUpperCase() === code);
          if (match) {
            const disc = match.discount_type === "percent"
              ? `−${match.discount_value}%`
              : `−€ ${Number(match.discount_value).toFixed(2)}`;
            setCouponAutoFire(true);
            toast.success(`✓ Coupon ${code} · ${match.name} (${disc}) — inserisci l'importo per registrare automaticamente`, { duration: 4500 });
          } else {
            setCouponAutoFire(false);
            const { data: all } = await api.get(`/customers/${customer.id}/coupons`);
            const owned = (all || []).find((r) => (r?.coupon?.code || "").toUpperCase() === code);
            if (owned) toast.warning(`⚠ Coupon ${code} non attivo ora: ${owned.coupon?.name || ""}`, { duration: 5000 });
            else toast.error(`✗ Coupon ${code} non assegnato a questo cliente`, { duration: 5000 });
          }
        } catch (_) {
          toast.success(`Coupon letto: ${code}`);
        }
      } else {
        setCouponAutoFire(false);
        toast.info(`Coupon ${code} letto — seleziona prima il cliente per applicarlo`);
      }
      return;
    }
    setQuery(value);
    toast.success(`QR letto: ${value}`);
    search(value);
  };

  const receiptOpts = async (earn, cust) => {
    // Resolve current branch: operator selection (admin) → user branch (staff) → transaction branch.
    const txBranchId = earn?.transaction?.branch_id || null;
    const branchId = operatorBranchId || user?.branch_id || txBranchId || "";
    let branch = branches.find((b) => b.id === branchId) || null;
    if (!branch && branchId) {
      try {
        const { data } = await api.get("/branches");
        setBranches(data || []);
        branch = (data || []).find((b) => b.id === branchId) || null;
      } catch (_) { /* ignore */ }
    }
    let activeCoupons = [];
    try {
      const { data } = await api.get(`/customers/${cust.id}/coupons/active`);
      activeCoupons = data || [];
    } catch (_) { activeCoupons = []; }
    return { earn, customer: cust, branch, settings, multiplier, activeCoupons };
  };

  const handlePrintReceipt = async ({ preview = false, thermal = false } = {}) => {
    if (!lastEarn || !customer) return;
    const opts = await receiptOpts(lastEarn, customer);
    if (thermal) {
      try { await printPoints(opts); toast.success("Scontrino inviato alla stampante termica"); }
      catch (e) { if (e?.name !== "NotFoundError") toast.error(e?.message || "Stampa termica non riuscita"); }
    } else if (preview) {
      setPreviewHtml(buildReceiptHtml({ ...opts, autoPrint: false }));
      setPreviewOpen(true);
    } else {
      openReceiptPrintWindow(opts);
    }
  };

  const openCouponPicker = async () => {
    if (!customer) { toast.error("Seleziona prima un cliente"); return; }
    setCouponPickerOpen(true);
    setLoadingCoupons(true);
    try {
      const { data } = await api.get(`/customers/${customer.id}/coupons/active`);
      setCouponPickerRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
      setCouponPickerRows([]);
    } finally { setLoadingCoupons(false); }
  };

  const openCouponScan = () => {
    if (!customer) { toast.error("Seleziona prima un cliente"); return; }
    setScanMode("coupon");
    setScanOpen(true);
  };

  useEffect(() => {
    if (!customer?.id || collections.length <= 1) { setCustCollections([]); return; }
    api.get(`/customers/${customer.id}/collections`).then((r) => setCustCollections(r.data || [])).catch(() => setCustCollections([]));
  }, [customer?.id, customer?.points, customer?.collection_points, collections.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshCustomer = async () => {
    if (!customer) return;
    const { data } = await api.get(`/customers/${customer.id}`);
    setCustomer(data);
  };

  const openEarnConfirm = () => {
    if (!customer) return toast.error("Seleziona un cliente");
    if (items.length === 0) {
      const eur = parseFloat(amount);
      if (!eur || eur <= 0) return toast.error("Aggiungi un prodotto o inserisci un importo");
    }
    setConfirmOpen(true);
  };

  const confirmEarn = async () => {
    if (!customer) return;
    const payload = { customer_id: customer.id };
    if (isAdmin && operatorBranchId) payload.branch_id = operatorBranchId;
    if (items.length > 0) {
      payload.items = items.map((it) => ({
        product_id: it.product_id || null,
        name: it.name,
        sku: it.sku || null,
        qty: it.qty,
        price: it.price,
        daily_deal: !!it.daily_deal,
        list_price: it.list_price ?? null,
      }));
    } else {
      payload.amount_eur = parseFloat(amount);
    }
    if (couponCode.trim()) payload.coupon_code = couponCode.trim().toUpperCase();
    if (parseFloat(cashbackUse) > 0) payload.cashback_use = parseFloat(cashbackUse);
    if (collections.length > 1) payload.collection_ids = selectedCollIds;
    setConfirming(true);
    try {
      const { data } = await api.post("/transactions/earn", payload);
      setCustomer(data.customer);
      setAmount("");
      setCouponCode("");
      setCashbackUse("");
      setCouponAutoFire(false);
      setItems([]);
      setLastEarn(data);
      setConfirmOpen(false);
      autoPrint("points", async () => printPoints(await receiptOpts(data, data.customer))).then((ok) => ok && toast.success("Scontrino inviato alla stampante termica"));
      toast.success((data.collections || []).length > 1 ? data.collections.map((c) => `+${c.points} pt ${c.name}`).join(" · ") : `+${(data.collections || [])[0]?.points ?? data.transaction.points_delta} pt${data.coupon ? ` (coupon -€${data.coupon.discount_eur.toFixed(2)})` : ""}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConfirming(false); }
  };

  const addItem = (product) => {
    const existing = items.findIndex((it) => it.product_id === product.id);
    if (existing >= 0) {
      const clone = [...items];
      clone[existing].qty += 1;
      setItems(clone);
    } else {
      const gallery = (product.images && product.images.length) ? product.images : (product.image_url ? [product.image_url] : []);
      const thumbs = product.image_thumbs && product.image_thumbs.length ? product.image_thumbs : gallery;
      const isDeal = !!(dailyProduct && dailyProduct.id === product.id && dailyProduct.discount_pct > 0);
      setItems([...items, {
        product_id: product.id,
        name: product.name,
        sku: product.sku,
        qty: 1,
        price: isDeal ? dailyProduct.price_today : (product.price || 0),
        list_price: product.price || 0,
        daily_deal: isDeal,
        images: gallery,
        image_thumbs: thumbs,
      }]);
      if (isDeal) toast.success(`✨ ${dailyProduct.label}: sconto -${dailyProduct.discount_pct}% applicato`);
      // If product has a gallery, briefly preview it so staff sees exactly what to prepare
      if (gallery.length > 0) setGalleryPreview(product);
    }
    setProductPickOpen(false);
  };
  const updateItem = (idx, patch) => {
    const clone = [...items];
    clone[idx] = { ...clone[idx], ...patch };
    setItems(clone);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const itemsTotal = useMemo(
    () => items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.price) || 0), 0),
    [items]
  );

  const redeem = async (rewardId, option = null) => {
    if (!customer) return;
    const base = rewards.find((r) => r.id === rewardId);
    if (!base) return;
    const reward = base.redemption_mode === "both"
      ? (option === "cash" ? { ...base, points_cost: base.points_cost_cash || base.points_cost } : { ...base, contribution_eur: 0 })
      : base;
    const contrib = reward.contribution_eur > 0 ? ` + € ${reward.contribution_eur.toFixed(2)} contributo` : "";
    const rColl = collections.find((c) => c.id === (reward.collection_id || defaultColl?.id));
    const bal = collBalance(reward.collection_id);
    const msg = `Confermi il riscatto di "${reward.name}" per ${customer.name}?\n\n` +
                `Costo: ${reward.points_cost} pt${contrib}\n` +
                (rColl && !rColl.is_default ? `Raccolta: ${rColl.name}\n` : "") +
                `Saldo attuale: ${bal} pt\n` +
                `Nuovo saldo: ${bal - reward.points_cost} pt`;
    if (!window.confirm(msg)) return;
    try {
      const payload = { customer_id: customer.id, reward_id: rewardId };
      if (base.redemption_mode === "both") payload.option = option || "points";
      if (isAdmin && operatorBranchId) payload.branch_id = operatorBranchId;
      const { data } = await api.post("/transactions/redeem", payload);
      setCustomer(data.customer);
      toast.success("Premio riscattato 🎉");
      autoPrint("redeem", async () => printReward({ title: "PREMIO RISCATTATO", rewardName: reward.name, customer: data.customer, pointsDelta: data.transaction?.points_delta, newBalance: data.customer?.points, contribution: data.contribution_eur, settings, branch: (await receiptOpts(data, data.customer)).branch }))
        .then((ok) => ok && toast.success("Ricevuta inviata alla stampante termica"));
      loadCtx();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const undoLastEarn = async () => {
    if (!lastEarn?.transaction?.id) return;
    const pt = lastEarn.transaction.points_delta;
    if (!window.confirm(
      `Annullare l'ultimo movimento?\n\n` +
      `Importo: € ${lastEarn.final_amount.toFixed(2)}\n` +
      `Punti da stornare: -${pt}\n\n` +
      `Il saldo del cliente tornerà al valore precedente.`
    )) return;
    try {
      const { data } = await api.post(`/transactions/${lastEarn.transaction.id}/pos-cancel`, {
        reason: "Annullo dal negozio",
      });
      setCustomer(data.customer);
      setLastEarn(null);
      toast.success(`Movimento annullato · storno ${data.transaction.points_delta > 0 ? "+" : ""}${-pt} punti`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const multiplier = campaign?.multiplier || 1;
  const previewBase = items.length > 0 ? itemsTotal : parseFloat(amount || "0");
  const previewPoints = previewBase ? Math.round(previewBase * multiplier) : 0;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="counter-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red">NEGOZIO POS</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Registra un movimento</h1>
          <p className="text-zinc-400 mt-1">Cerca la carta o il telefono, poi aggiungi punti o riscatta premi.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          {isAdmin && (
            <div
              className={`card-elev p-3 sm:min-w-[240px] ${operatorBranchId ? "border-amber-500/50 bg-amber-500/5" : ""}`}
              data-testid="counter-operator-branch"
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
                <ScanLine className="h-3 w-3 text-amber-300" /> Operi come sede
              </div>
              <select
                data-testid="counter-operator-branch-select"
                value={operatorBranchId}
                onChange={(e) => setOperatorBranchId(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md h-9 px-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500"
              >
                <option value="">— Nessuna (usa sede cliente) —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {operatorBranchId && (
                <div className="text-[10px] text-amber-300 mt-1" data-testid="counter-operator-branch-hint">
                  Tutti i movimenti saranno associati a questa sede.
                </div>
              )}
            </div>
          )}
          {campaign && (
            <div className="card-elev p-4 glow-gold flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-amber-300" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300">Campagna attiva</div>
                <div className="font-heading font-bold">{campaign.name} · {campaign.multiplier}× punti</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* SEARCH */}
        <div className="card-elev p-5 lg:col-span-2 space-y-4">
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-[color:var(--vivi)]" /> Cerca cliente
          </h3>
          <div className="flex gap-2">
            <Input
              data-testid="counter-search-input"
              placeholder="Nome, cognome, carta o telefono"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              className="bg-zinc-950 border-zinc-800 h-11"
            />
            <Button data-testid="counter-search-button" onClick={() => search()} disabled={searching} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
              <Search className="h-4 w-4" />
            </Button>
            <Button data-testid="counter-scan-button" onClick={() => setScanOpen(true)} variant="outline" className="border-zinc-800 h-11" title="Scansiona QR carta">
              <QrCode className="h-4 w-4" />
            </Button>
            <Button data-testid="counter-pickup-button" onClick={() => setPickup({ code: isPickupCode(query) ? query.toUpperCase() : "" })} variant="outline" className="border-emerald-800 text-emerald-300 h-11" title="Ritiro premio con codice/QR">
              <Gift className="h-4 w-4" />
            </Button>
            <ThermalQuickButton />
          </div>
          <PickupRedeem open={!!pickup} initialCode={pickup?.code || ""} branchId={operatorBranchId || user?.branch_id || undefined} onClose={() => setPickup(null)}
            onDelivered={(d) => { if (customer?.id === d.customer?.id) setCustomer(d.customer); }} />

          {/* Multi-match results — clickable list to pick the intended customer */}
          {searchResults.length > 0 && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 divide-y divide-zinc-800 max-h-80 overflow-y-auto" data-testid="counter-search-results">
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold sticky top-0 bg-zinc-950 border-b border-zinc-800">
                {searchResults.length} risultati · seleziona un cliente
              </div>
              {searchResults.map((c) => (
                <button
                  key={c.id}
                  data-testid={`counter-result-${c.id}`}
                  onClick={() => pickResult(c)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-900 transition-colors flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono truncate">
                      {c.card_number} · {c.phone}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-amber-300 font-mono font-bold text-sm">{c.points || 0}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest">punti</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <QrScanner
            open={scanOpen}
            onClose={() => { setScanOpen(false); setScanMode("card"); }}
            onDecoded={onScanDecoded}
          />

          <button
            type="button"
            data-testid="counter-new-card-button"
            onClick={() => navigate("/rilascio-card")}
            className="counter-new-card w-full rounded-xl p-3 flex items-center gap-3 text-left"
          >
            <span className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <CreditCard className="h-5 w-5" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-heading font-bold text-base leading-tight">Rilascia nuova carta</span>
              <span className="block text-xs opacity-85">Apri il modulo completo: tipo carta, dati cliente, consensi GDPR</span>
            </span>
            <Plus className="h-5 w-5 shrink-0" />
          </button>
        </div>

        {/* CUSTOMER DETAIL */}
        <div className="card-elev p-5 lg:col-span-3">
          {!customer ? (
            <div className="h-full flex items-center justify-center text-zinc-500 py-12">
              Nessun cliente selezionato.
            </div>
          ) : (
            <div className="space-y-5" data-testid="counter-customer-panel">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    Cliente
                    {customer.wallet_saved && (
                      <span
                        data-testid="counter-customer-wallet-badge"
                        className="pill pill-gold bg-black/50 border border-amber-400/60 text-amber-300 text-[10px] tracking-wider"
                        title={`Wallet: ${customer.wallet_last_method || "attivo"}`}
                      >
                        <Wallet className="h-3 w-3" /> WALLET ⭐
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-2xl">{customer.name}</h3>
                  <div className="text-sm text-zinc-400 font-mono">
                    Carta {customer.card_number} · {customer.phone}
                  </div>
                  <div className="mt-2"><ElasticHistoryButton cardNumber={customer.card_number} testid="counter-crm-history" /></div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Saldo</div>
                  <div className="metric-value text-4xl text-amber-300">{customer.points}</div>
                  {customer.card_status === "blocked" && <span className="pill pill-red mt-1" data-testid="counter-card-blocked">Carta bloccata — sostituire da Clienti</span>}
                  {customer.group && <div className="text-xs font-semibold text-sky-300 mt-1" data-testid="counter-group-balance">Gruppo {customer.group.name}: {customer.group.balance} pt condivisi</div>}
                  <div className="text-xs text-zinc-500">punti</div>
                  {custCollections.filter((c) => !c.is_default).map((c) => (
                    <div key={c.id} className="mt-1 text-xs font-semibold flex items-center justify-end gap-1" style={{ color: c.color }} data-testid={`counter-collection-balance-${c.id}`}>
                      <span className="inline-block h-2 w-2 rounded-full" style={{ background: c.color }} /> {c.name}: {c.balance} pt{c.expiry?.date ? <span className="text-zinc-500 font-normal"> · scad. {c.expiry.date.split("-").reverse().join("/")}</span> : null}
                    </div>
                  ))}
                  {cbCfg?.enabled && (
                    <div className="mt-1 text-xs font-semibold text-emerald-300" data-testid="counter-cashback-balance">
                      Cashback € {Number(customer.cashback_balance || 0).toFixed(2)}
                      {Number(customer.cashback_balance || 0) >= cbCfg.use_threshold && Number(customer.cashback_balance || 0) > 0 ? <span className="pill pill-mint ml-1 text-[10px]">utilizzabile</span> : <span className="text-zinc-500 font-normal"> · soglia € {Number(cbCfg.use_threshold).toFixed(2)}</span>}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800">
                  <div className="flex items-center gap-2 mb-3 text-[color:var(--vivi)] font-semibold">
                    <Coins className="h-4 w-4" /> Inserimento movimento
                  </div>

                  {/* Product line items */}
                  {items.length > 0 && (
                    <div className="mb-3 space-y-1" data-testid="counter-items-list">
                      {items.map((it, idx) => (
                        <div key={idx} className="flex items-center gap-1 text-xs bg-black rounded p-1.5 border border-zinc-800" data-testid={`counter-item-${idx}`}>
                          <span className="flex-1 truncate flex items-center gap-1">
                            {it.name}
                            {it.daily_deal && (
                              <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 rounded-full bg-amber-400 text-zinc-950 text-[9px] font-black uppercase" data-testid={`counter-item-deal-${idx}`}>
                                <Sparkles className="h-2.5 w-2.5" /> -{dailyProduct?.discount_pct}%
                              </span>
                            )}
                          </span>
                          <Input
                            type="number" min="0.5" step="0.5"
                            value={it.qty}
                            onChange={(e) => updateItem(idx, { qty: parseFloat(e.target.value || "1") })}
                            className="w-14 h-7 bg-zinc-950 border-zinc-800 text-xs px-1 text-center"
                            data-testid={`counter-item-qty-${idx}`}
                          />
                          <span className="text-zinc-500">×</span>
                          <Input
                            type="number" min="0" step="0.01"
                            value={it.price}
                            onChange={(e) => updateItem(idx, { price: parseFloat(e.target.value || "0") })}
                            className="w-16 h-7 bg-zinc-950 border-zinc-800 text-xs px-1 text-center"
                            data-testid={`counter-item-price-${idx}`}
                          />
                          <span className="w-14 text-right font-mono text-amber-300 text-xs">
                            € {(it.qty * it.price).toFixed(2)}
                          </span>
                          <button onClick={() => removeItem(idx)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`counter-item-remove-${idx}`}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <div className="flex justify-between text-xs font-mono pt-1 border-t border-zinc-900 mt-1">
                        <span className="text-zinc-500">Totale righe</span>
                        <span className="font-bold text-amber-300" data-testid="counter-items-total">€ {itemsTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mb-2">
                    <Button
                      data-testid="counter-add-product"
                      variant="outline"
                      onClick={() => setProductPickOpen(true)}
                      className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex-1 h-9"
                    >
                      <Package className="h-4 w-4 mr-1" /> Aggiungi prodotto
                    </Button>
                  </div>

                  {activeColls.length > 1 && (
                    <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="counter-collections-picker">
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">Accredita su</span>
                      {activeColls.map((c) => {
                        const on = selectedCollIds.includes(c.id);
                        return (
                          <button key={c.id} type="button" onClick={() => toggleColl(c.id)} data-testid={`counter-collection-toggle-${c.id}`}
                            className={`pill cursor-pointer text-[11px] transition ${on ? "" : "pill-zinc opacity-60"}`} style={on ? { background: c.color, color: "#111" } : undefined}>
                            {on ? "✓ " : ""}{c.name}{c.earn_rule?.min_amount > 0 ? ` · da ${Number(c.earn_rule.min_amount).toFixed(0)} €` : ""}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      data-testid="counter-amount-input"
                      type="number" step="0.01" min="0"
                      placeholder={items.length > 0 ? "Auto (somma righe)" : "Importo €"}
                      value={items.length > 0 ? "" : amount}
                      disabled={items.length > 0}
                      onChange={(e) => setAmount(e.target.value)}
                      className="bg-black border-zinc-800 disabled:opacity-60"
                    />
                    <Button
                      data-testid="counter-earn-button"
                      onClick={openEarnConfirm}
                      className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
                    >
                      +{previewPoints} pt
                    </Button>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <div className="relative flex-1">
                      <Ticket className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
                      <Input
                        data-testid="counter-coupon-input"
                        placeholder="Codice coupon (opz.)"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="bg-black border-zinc-800 pl-7 font-mono text-sm h-9"
                      />
                    </div>
                    <Button
                      data-testid="counter-coupon-scan"
                      size="sm"
                      variant="outline"
                      onClick={openCouponScan}
                      title="Scansiona QR coupon"
                      className="border-zinc-800 h-9 px-2 hover:border-amber-500/60 hover:text-amber-300"
                    >
                      <ScanLine className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid="counter-coupon-picker"
                      size="sm"
                      variant="outline"
                      onClick={openCouponPicker}
                      title="Coupon attivi disponibili del cliente"
                      className="border-zinc-800 h-9 px-2 hover:border-amber-500/60 hover:text-amber-300"
                    >
                      <Gift className="h-4 w-4" />
                    </Button>
                    {couponCode && (
                      <Button size="sm" variant="ghost" onClick={() => setCouponCode("")} className="h-9 text-zinc-500">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {cbCfg?.enabled && cbCfg.use_in_pos && Number(customer.cashback_balance || 0) > 0 && Number(customer.cashback_balance || 0) >= cbCfg.use_threshold && (
                    <div className="mt-2 flex items-center gap-2" data-testid="counter-cashback-row">
                      <div className="relative flex-1">
                        <Coins className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-emerald-400" />
                        <Input
                          data-testid="counter-cashback-input"
                          type="number" step="0.01" min="0" max={Number(customer.cashback_balance).toFixed(2)}
                          placeholder={`Usa credito cashback (max € ${Number(customer.cashback_balance).toFixed(2)})`}
                          value={cashbackUse}
                          onChange={(e) => setCashbackUse(e.target.value)}
                          className="bg-black border-emerald-900 pl-7 font-mono text-sm h-9"
                        />
                      </div>
                      <Button size="sm" variant="outline" className="border-emerald-800 h-9 text-emerald-300" data-testid="counter-cashback-all"
                        onClick={() => setCashbackUse(Math.min(Number(customer.cashback_balance), items.length ? itemsTotal : parseFloat(amount) || Number(customer.cashback_balance)).toFixed(2))}>Tutto</Button>
                      {cashbackUse && <Button size="sm" variant="ghost" onClick={() => setCashbackUse("")} className="h-9 text-zinc-500"><X className="h-4 w-4" /></Button>}
                    </div>
                  )}
                  <p className="text-[11px] text-zinc-500 mt-2">
                    1 pt / € · Moltiplicatore campagna: <span className="font-mono">{multiplier}×</span>
                  </p>
                  {lastEarn && (
                    <div className="mt-3 p-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-xs" data-testid="counter-last-earn">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-emerald-300">Scontrino registrato</div>
                        <div className="flex items-center gap-1">
                          <Button
                            data-testid="counter-preview-receipt"
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-sky-300 hover:text-sky-100"
                            onClick={() => handlePrintReceipt({ preview: true })}
                            title="Anteprima scontrino"
                          >
                            <Eye className="h-3 w-3 mr-1" /> Anteprima
                          </Button>
                          <Button
                            data-testid="counter-print-receipt"
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-emerald-300 hover:text-emerald-100"
                            onClick={() => handlePrintReceipt()}
                          >
                            <Printer className="h-3 w-3 mr-1" /> Stampa
                          </Button>
                          {thermalSupported() && (
                            <Button
                              data-testid="counter-print-receipt-thermal"
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-sky-300 hover:text-sky-100"
                              onClick={() => handlePrintReceipt({ thermal: true })}
                              title="Stampa su termica Bluetooth"
                            >
                              <Bluetooth className="h-3 w-3 mr-1" /> Termica
                            </Button>
                          )}
                          <Button
                            data-testid="counter-undo-earn"
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-red-300 hover:text-red-100 hover:bg-red-500/10"
                            onClick={undoLastEarn}
                            title="Annulla il movimento appena registrato (entro 5 minuti)"
                          >
                            <Undo2 className="h-3 w-3 mr-1" /> Annulla
                          </Button>
                        </div>
                      </div>
                      <div className="font-mono flex justify-between mt-1">
                        <span className="text-zinc-400">Importo</span><span>€ {lastEarn.original_amount.toFixed(2)}</span>
                      </div>
                      {lastEarn.coupon && (
                        <div className="font-mono flex justify-between text-red-300">
                          <span>Sconto {lastEarn.coupon.code}</span><span>-€ {lastEarn.coupon.discount_eur.toFixed(2)}</span>
                        </div>
                      )}
                      {lastEarn.cashback?.used > 0 && (
                        <div className="font-mono flex justify-between text-red-300" data-testid="counter-last-cashback-used">
                          <span>Credito cashback usato</span><span>-€ {lastEarn.cashback.used.toFixed(2)}</span>
                        </div>
                      )}
                      {lastEarn.cashback?.earned > 0 && (
                        <div className="font-mono flex justify-between text-emerald-300" data-testid="counter-last-cashback-earned">
                          <span>Cashback maturato</span><span>+€ {lastEarn.cashback.earned.toFixed(2)} (saldo € {lastEarn.cashback.balance.toFixed(2)})</span>
                        </div>
                      )}
                      <div className="font-mono flex justify-between font-bold border-t border-emerald-500/20 mt-1 pt-1">
                        <span>Totale</span><span>€ {lastEarn.final_amount.toFixed(2)}</span>
                      </div>
                      <div className="text-zinc-500 mt-1" data-testid="counter-last-earn-points">{(lastEarn.collections || []).length > 1 ? lastEarn.collections.map((c) => `+${c.points} pt ${c.name}`).join(" · ") : `+${(lastEarn.collections || [])[0]?.points ?? lastEarn.transaction.points_delta} punti accreditati`}</div>
                    </div>
                  )}
                </div>

                <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-emerald-300 font-semibold flex items-center gap-2"><Gift className="h-4 w-4" /> Riscatta premio</span>
                    <button onClick={refreshCustomer} className="text-zinc-500 hover:text-zinc-300"><RefreshCw className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto pb-1 pr-0.5">
                    {rewards.length === 0 && <div className="text-xs text-zinc-500">Nessun premio disponibile.</div>}
                    {rewards.map((r) => {
                      const rColl = collections.find((c) => c.id === (r.collection_id || defaultColl?.id));
                      const bal = collBalance(r.collection_id);
                      const collOk = r.stock !== 0 && (!rColl || rColl.status === "active");
                      const enabled = bal >= r.points_cost && collOk;
                      if (r.redemption_mode === "both") {
                        const cashCost = r.points_cost_cash || r.points_cost;
                        return (
                          <div key={r.id} className="w-full p-2 rounded-lg border border-zinc-800 space-y-1" data-testid={`counter-reward-both-${r.id}`}>
                            <div className="text-sm font-medium truncate">{r.name}</div>
                            <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-2"><span>Scegli: solo punti oppure punti + contributo</span>{rColl && !rColl.is_default && <span style={{ color: rColl.color }} data-testid={`counter-reward-collection-${r.id}`}>● {rColl.name} ({bal} pt)</span>}</div>
                            <div className="grid grid-cols-2 gap-1">
                              <button data-testid={`counter-redeem-${r.id}`} disabled={!enabled} onClick={() => redeem(r.id, "points")} className={`rounded-md border px-2 py-1 text-xs font-semibold ${enabled ? "border-emerald-800 text-emerald-300 hover:bg-emerald-500/10" : "border-zinc-900 opacity-40 cursor-not-allowed"}`}>{r.points_cost} pt</button>
                              <button data-testid={`counter-redeem-cash-${r.id}`} disabled={!(bal >= cashCost && collOk)} onClick={() => redeem(r.id, "cash")} className={`rounded-md border px-2 py-1 text-xs font-semibold ${bal >= cashCost && collOk ? "border-amber-700 text-amber-300 hover:bg-amber-500/10" : "border-zinc-900 opacity-40 cursor-not-allowed"}`}>{cashCost} pt + € {Number(r.contribution_eur || 0).toFixed(2)}</button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <button
                          key={r.id}
                          data-testid={`counter-redeem-${r.id}`}
                          disabled={!enabled}
                          onClick={() => redeem(r.id)}
                          className={`w-full flex items-center justify-between p-2 rounded-lg border text-left transition
                            ${enabled ? "border-emerald-800 hover:bg-emerald-500/5" : "border-zinc-900 opacity-40 cursor-not-allowed"}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{r.name}</div>
                            <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-2">
                              {r.contribution_eur > 0 ? <span className="text-red-300">+ € {r.contribution_eur.toFixed(2)} contributo</span> : "Solo punti"}
                              {rColl && !rColl.is_default && <span style={{ color: rColl.color }} data-testid={`counter-reward-collection-${r.id}`}>● {rColl.name} ({bal} pt)</span>}
                            </div>
                          </div>
                          <span className="pill pill-mint font-mono">{r.points_cost} pt</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product picker */}
      <Dialog open={productPickOpen} onOpenChange={setProductPickOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Seleziona prodotto</DialogTitle></DialogHeader>
          {products.length === 0 ? (
            <div className="text-sm text-zinc-500 py-4">
              Nessun prodotto in catalogo. Aggiungili in <strong>Prodotti</strong>.
            </div>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {products.map((p) => {
                const gallery = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
                const thumbs = (p.image_thumbs && p.image_thumbs.length) ? p.image_thumbs : gallery;
                const cover = thumbs[0];
                return (
                  <div
                    key={p.id}
                    className="w-full flex items-center gap-3 p-2 rounded-md border border-zinc-800 hover:bg-zinc-900/50"
                    data-testid={`counter-pick-row-${p.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => cover && setGalleryPreview(p)}
                      className="h-11 w-11 rounded-md overflow-hidden bg-zinc-950 border border-zinc-800 shrink-0 relative"
                      data-testid={`counter-pick-thumb-${p.id}`}
                    >
                      {cover ? (
                        <img src={resolveImageUrl(cover)} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-600"><ImageIcon className="h-4 w-4" /></div>
                      )}
                      {gallery.length > 1 && (
                        <span className="absolute bottom-0 right-0 bg-black/70 text-[9px] px-1 text-amber-300 font-mono rounded-tl">
                          <Camera className="h-2.5 w-2.5 inline" /> {gallery.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => addItem(p)}
                      data-testid={`counter-pick-product-${p.id}`}
                      className="flex-1 flex items-center justify-between text-left"
                    >
                      <div>
                        <div className="text-sm font-medium">{p.name}</div>
                        <div className="text-[11px] text-zinc-500">{p.category || "—"} · {p.sku || ""}</div>
                      </div>
                      <span className="pill pill-gold font-mono">€ {(p.price ?? 0).toFixed(2)}</span>
                      {dailyProduct && dailyProduct.id === p.id && (
                        <span className="ml-1 pill bg-amber-400 text-zinc-950 font-bold text-[10px] inline-flex items-center gap-0.5" data-testid={`counter-pick-deal-${p.id}`}>
                          <Sparkles className="h-3 w-3" /> {dailyProduct.discount_pct > 0 ? `Oggi -${dailyProduct.discount_pct}%` : "Del giorno"}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Coupon picker dialog — customer's currently active + unused coupons */}
      <Dialog open={couponPickerOpen} onOpenChange={setCouponPickerOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md max-h-[85vh] overflow-y-auto" data-testid="counter-coupon-picker-dialog" aria-describedby="counter-coupon-picker-desc">
          <DialogHeader><DialogTitle>Coupon attivi del cliente</DialogTitle></DialogHeader>
          <p id="counter-coupon-picker-desc" className="sr-only">Elenco dei coupon attivi e non usati per il cliente selezionato; tocca una card per riempire il codice coupon nel movimento.</p>
          {customer && (
            <p className="text-[11px] text-zinc-500 -mt-2 mb-2">
              {customer.name} · <span className="font-mono">{customer.card_number}</span>
            </p>
          )}
          {loadingCoupons ? (
            <div className="py-8 text-center text-sm text-zinc-500">Caricamento…</div>
          ) : couponPickerRows.length === 0 ? (
            <div className="py-8 text-center text-sm text-zinc-500" data-testid="counter-coupon-picker-empty">
              Nessun coupon attivo disponibile per questo cliente.
            </div>
          ) : (
            <div className="space-y-2">
              {couponPickerRows.map((cp) => {
                const isPct = cp.discount_type === "percent";
                const discountLabel = isPct
                  ? `-${cp.discount_value}%`
                  : `-€ ${Number(cp.discount_value).toFixed(2)}`;
                return (
                  <button
                    key={cp.id}
                    type="button"
                    data-testid={`counter-coupon-pick-${cp.id}`}
                    onClick={() => {
                      setCouponCode(cp.code);
                      setCouponPickerOpen(false);
                      toast.success(`Coupon selezionato: ${cp.code}`);
                    }}
                    className="w-full text-left rounded-xl border border-zinc-800 bg-zinc-950 p-3 hover:border-amber-500/60 active:scale-[0.98] transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-semibold text-sm text-zinc-100 truncate">{cp.name}</div>
                        <div className="font-mono text-[11px] text-amber-300 mt-0.5">{cp.code}</div>
                        {(cp.min_spend > 0 || cp.valid_until || cp.wallet_only || cp.single_use) && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {cp.min_spend > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400">
                                min € {Number(cp.min_spend).toFixed(2)}
                              </span>
                            )}
                            {cp.valid_until && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400">
                                fino {String(cp.valid_until).slice(0, 10)}
                              </span>
                            )}
                            {cp.wallet_only && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300">
                                Wallet
                              </span>
                            )}
                            {cp.single_use && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400">
                                Uso singolo
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="font-heading text-lg font-bold text-emerald-300">{discountLabel}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm earn dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !confirming && setConfirmOpen(o)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="counter-confirm-dialog">
          <DialogHeader><DialogTitle>Conferma movimento</DialogTitle></DialogHeader>
          {customer && (
            <div className="space-y-4 text-sm">
              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Cliente</div>
                <div className="font-heading font-bold text-lg">{customer.name}</div>
                <div className="text-xs text-zinc-500 font-mono">Carta {customer.card_number} · {customer.phone}</div>
              </div>

              {items.length > 0 ? (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Righe prodotti</div>
                  {items.map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-md bg-zinc-950 border border-zinc-800 text-xs" data-testid={`confirm-item-${idx}`}>
                      {(it.image_thumbs?.[0] || it.images?.[0]) && (
                        <button
                          type="button"
                          onClick={() => setGalleryPreview({ name: it.name, images: it.images, image_thumbs: it.image_thumbs })}
                          className="h-10 w-10 rounded-md overflow-hidden bg-black shrink-0 mr-2 border border-zinc-800"
                          data-testid={`confirm-item-thumb-${idx}`}
                        >
                          <img src={resolveImageUrl(it.image_thumbs?.[0] || it.images?.[0])} alt={it.name} className="w-full h-full object-cover" />
                        </button>
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{it.name}</div>
                        <div className="text-zinc-500">{it.qty} × € {parseFloat(it.price).toFixed(2)}</div>
                      </div>
                      <span className="font-mono text-amber-300">€ {(it.qty * it.price).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-2 rounded-md bg-zinc-950 border border-zinc-800 text-xs text-zinc-400">
                  Importo libero (nessun prodotto selezionato)
                </div>
              )}

              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-1 font-mono text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Totale</span>
                  <span className="font-bold text-lg text-amber-300" data-testid="confirm-total">€ {previewBase.toFixed(2)}</span>
                </div>
                {couponCode && (
                  <div className="flex justify-between text-red-300 text-xs">
                    <span>Coupon</span><span>{couponCode.toUpperCase()}</span>
                  </div>
                )}
                <div className="flex justify-between text-emerald-300 border-t border-zinc-800 pt-1 mt-1">
                  <span>Punti da accreditare</span>
                  <span className="font-bold" data-testid="confirm-points">{activeColls.length > 1 ? `${selectedCollIds.length} raccolt${selectedCollIds.length === 1 ? "a" : "e"}` : `+${previewPoints} pt`}</span>
                </div>
                {activeColls.length > 1 && (
                  <ul className="text-[11px] text-zinc-400 space-y-0.5" data-testid="confirm-collections">
                    {activeColls.filter((c) => selectedCollIds.includes(c.id)).map((c) => <li key={c.id} className="flex justify-between"><span style={{ color: c.color }}>● {c.name}</span><span>{c.is_default ? `+${previewPoints} pt` : `${c.earn_rule?.min_amount > 0 ? `soglia ${Number(c.earn_rule.min_amount).toFixed(2)} € · ` : ""}${c.earn_rule?.points_per_euro || 1} pt/€`}</span></li>)}
                  </ul>
                )}
                {multiplier > 1 && (
                  <div className="text-[10px] text-amber-300 text-right">Campagna {multiplier}× attiva</div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid="counter-confirm-cancel"
                  variant="outline"
                  className="flex-1 border-zinc-800"
                  disabled={confirming}
                  onClick={() => setConfirmOpen(false)}
                >
                  Annulla
                </Button>
                <Button
                  data-testid="counter-confirm-submit"
                  disabled={confirming}
                  onClick={confirmEarn}
                  className="flex-1 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
                >
                  {confirming ? "Registrazione…" : "Conferma e registra"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product gallery preview lightbox */}
      <Dialog open={!!galleryPreview} onOpenChange={(o) => !o && setGalleryPreview(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-2xl p-0 overflow-hidden" data-testid="counter-gallery-lightbox">
          <DialogHeader className="p-4 pb-2"><DialogTitle>{galleryPreview?.name}</DialogTitle></DialogHeader>
          <CounterGalleryBody item={galleryPreview} />
        </DialogContent>
      </Dialog>

      {/* Anteprima scontrino: iframe con l'HTML costruito da lib/receipt.js.
          Il pulsante "Stampa ora" apre la finestra reale che triggera window.print. */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md p-0 overflow-hidden" data-testid="counter-receipt-preview-dialog">
          <DialogHeader className="p-4 pb-2"><DialogTitle>Anteprima scontrino</DialogTitle></DialogHeader>
          <iframe
            title="Anteprima scontrino"
            data-testid="counter-receipt-preview-iframe"
            srcDoc={previewHtml}
            className="w-full bg-white"
            style={{ height: "70vh", border: 0 }}
          />
          <div className="p-3 border-t border-zinc-800 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-zinc-800" data-testid="counter-receipt-preview-close">
              Chiudi
            </Button>
            <Button
              data-testid="counter-receipt-preview-print"
              onClick={() => { setPreviewOpen(false); handlePrintReceipt(); }}
              className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
            >
              <Printer className="h-4 w-4 mr-2" /> Stampa ora
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CounterGalleryBody({ item }) {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => { setIdx(0); }, [item?.id, item?.name]);
  if (!item) return null;
  const images = (item.images && item.images.length) ? item.images : (item.image_url ? [item.image_url] : []);
  const thumbs = (item.image_thumbs && item.image_thumbs.length) ? item.image_thumbs : images;
  if (!images.length) return <div className="p-6 text-center text-sm text-zinc-500">Nessuna foto disponibile</div>;
  const url = images[idx];
  return (
    <>
      <div className="relative bg-black">
        <img src={resolveImageUrl(url)} alt={item.name} className="w-full max-h-[60vh] object-contain bg-black" data-testid={`counter-gallery-img-${idx}`} />
        {images.length > 1 && (
          <>
            <button onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="counter-gallery-prev">‹</button>
            <button onClick={() => setIdx((i) => (i + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="counter-gallery-next">›</button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, i) => (
                <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-amber-300" : "bg-zinc-500/60"}`} />
              ))}
            </div>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="p-3 flex gap-2 overflow-x-auto bg-zinc-950 border-t border-zinc-800">
          {images.map((u, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`h-14 w-14 rounded-md overflow-hidden border ${i === idx ? "border-amber-400" : "border-zinc-800"} shrink-0`}>
              <img src={resolveImageUrl(thumbs[i] || u)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}
